import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { Injectable, NgZone, ChangeDetectorRef } from '@angular/core';
import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { Router } from '@angular/router';

import { ViewStateRecord } from "@common/data-models/records/viewstate-record";
import { NavigationGroup } from "@common/data-models/service/navigation-group";
import { NavigationSection } from "@common/data-models/service/navigation-section";
import { NavigationItem } from "@common/data-models/service/navigation-item";
import { NavigationLayoutTypes } from "@common/enums/navigation-layout-types";

import { ViewStateTypes } from "@common/enums/viewstate-types";
import { ViewStateFilterSerializer, ViewStateOptionsSerializer } from "@common/data-models/serializers/serializers";
import { SearchViewStateRecord } from "@common/data-models/records/search-viewstate-record";
import { Path } from "@common/data-models/complex-types/path";
import { DataStore } from "@common/data-models/data-store/data-store";
import { TopicViewStateRecord } from "@common/data-models/records/topic-viewstate-record";
import { SingleTopicViewStateRecord } from "@common/data-models/records/single-topic-viewstate-record";
import { AssignmentViewStateRecord } from "@common/data-models/records/assignment-viewstate-record";
import { SlideshowViewStateRecord } from "@common/data-models/records/slideshow-viewstate-record";
import { PSTFieldNames } from "@common/data-models/fields/pst-field-names";
import { ServiceResponse } from "@common/data-models/service/service-response";
import { ServiceRecord } from '@common/data-models/service/service-record';
import { ApiServiceRecord } from '@common/data-models/service/api-service-record';
import { RecordFactory } from "@common/data-models/records/record-factory";
import { Record } from '@common/data-models/records/record';

import { AsyncServiceBase } from "@common/services/async-service-base";
import { NomWebServicesJson } from "@common/services/nom-web-services-json";
import { VisualStateService } from "@common/services/visual-state-service";
import { LanguageSettings } from "@common/services/language-settings";
import { GlobalTablesService } from "@common/services/global-tables-service";
import { CustomCardFieldsService } from "@common/services/custom-card-fields-service";
import { LayoutState, ViewStateRecordsAlias } from "@common/services/layout-state";
import { TimeoutQueue } from '@common/services/timeout-queue';
import { ObjectTypes } from "@common/enums/object-types";
import { ManageTeamMembers } from '../components/manage-team-members/manage-team-members';


@Injectable({ providedIn: 'root' })
export class NavigationService extends AsyncServiceBase {
    public inEditTeamSettingsMode: boolean = false;
    public enterprisePlaybookTeam: string;
    public exitingTeamSettingsMode: boolean = false;
    public currentPlaybookTeam: string;
    public prevPlaybookTeam: string;

    public ApplicationNavigation: { [id: string]: Array<NavigationGroup> } = {};
    private ApplicationNavigationData: {
        [id: string]: {
            Default: NavigationItem,
            IsNavigationChanged: boolean
        }
    } = {};

    public Application: string = null;

    public Navigation: Array<NavigationGroup> = [];
    public NavigationSource = new BehaviorSubject<Array<NavigationGroup>>(this.Navigation);
    public NavigationObservable = this.NavigationSource.asObservable();


    public CurrentNavigationGroup: NavigationGroup = null;
    public CurrentNavigationSection: NavigationSection = null;
    public CurrentNavigationItem: NavigationItem = null;
    public CurrentNavigationItemSource = new Subject<NavigationItem>();
    public CurrentNavigationItemObservable = this.CurrentNavigationItemSource.asObservable();

    public NavigationItemsDict: { [id: string]: Array<string> } = {};

    private navigationSectionsSubscription: Subscription;

    //New view properties
    public newViewShown: { [id: string]: any } = { flag: false, event: null };
    private newViewShownSource = new Subject<any>();
    public newViewShownObservable = this.newViewShownSource.asObservable();

    //New template properties
    public newTemplateShown: { [id: string]: any } = { flag: false, event: null };
    private newTemplateShownSource = new Subject<any>();
    public newTemplateShownObservable = this.newTemplateShownSource.asObservable();

    public fromNotification: boolean = false;
    public SelectedNavigationItemLabel: string = "";

    public isAboutshown: any = { flag: false, event: null };
    public showAboutSource = new Subject<any>();
    public showAboutObservable = this.showAboutSource.asObservable();

    public currentListView: any = null;
    public currentListChangeDetectorRef: ChangeDetectorRef = null;

    constructor(
        public nomWebServicesJson: NomWebServicesJson,
        public globalTablesService: GlobalTablesService,
        public visualStateService: VisualStateService,
        public customCardFieldsService: CustomCardFieldsService,
        public languageSettings: LanguageSettings,
        public viewStateService: LayoutState,
        public router: Router,
        public ngZone: NgZone,
        public timeoutQueue: TimeoutQueue
    ) {
        super();

        this.globalTablesService.globalTablesLoaded.subscribe(() => {
            this.getNavigationAndResolve();
        });

        if (!window["NavigateToItem"]) {
            window["NavigateToItem"] = () => {

                this.ngZone.run(async () => {
                    try {
                        let navigationData = localStorage.getItem("navigationData");
                        if (navigationData) {
                            let objNavigationData = JSON.parse(navigationData);
                            this.hideAbout(null);
                            // let submenu: string = (this.CurrentNavigationItem.SectionPath) ? this.CurrentNavigationItem.SectionPath : "tmp";
                            // let item: string = (this.CurrentNavigationItem.ViewState.guid) ? this.CurrentNavigationItem.ViewState.guid : "tmp";
                            localStorage.removeItem("navigationData");
                            this.fromNotification = true;
                            if (objNavigationData["Type"] && objNavigationData["Type"] == ObjectTypes.TOPIC) {
                                this.router.navigate(['/PST', 'playbook', 'system', 'topic', objNavigationData["Guid"]]);
                            } else {
                                this.router.navigate(['/PST', 'playbook', 'system', 'assignment', objNavigationData["Guid"]]);
                            }
                        }
                    } catch (e) {
                        console.log("Failed to navigate to item ...")
                        console.log(e.message)
                    }
                })
            };
        }
    }

    public async getNavigationAndResolve(): Promise<any> {
        if (this.visualStateService.isDesktop) {
            this.initializeNavigationForDesktop();
        } else {
            this.initializeNavigation();
        }
        this.initializeResolve();
    }

    public async setNavigationItem(item: NavigationItem) {
        if (item) {
            this.CurrentNavigationItem = item;
            if (item.SectionID == NavigationSection.SYSTEM_SECTION_ID) {
                if (item.ViewState instanceof SearchViewStateRecord) {
                    this.SelectedNavigationItemLabel = "Search - " + item.ViewState.viewStateSearchQuery;
                } else {
                    this.SelectedNavigationItemLabel = item.ViewState.title;
                }
            } else {
                this.SelectedNavigationItemLabel = item.Label;
            }
            await this.viewStateService.setViewStateRecord(item.ViewState);


            this.CurrentNavigationGroup = this.CurrentNavigationSection = null;

            this.Navigation.every((group: NavigationGroup) => {
                group.Items.every((section: NavigationSection) => {
                    if (section.ID == item.SectionID) {
                        this.CurrentNavigationGroup = group;
                        this.CurrentNavigationSection = section;
                        return false;
                    }
                    return true;
                });
                if (this.CurrentNavigationGroup) return false;
                return true;
            });
        } else {
            this.CurrentNavigationGroup = this.CurrentNavigationSection = this.CurrentNavigationItem = null;
            await this.viewStateService.setViewStateRecord(null);
        }

        this.CurrentNavigationItemSource.next(this.CurrentNavigationItem);
    }

    // Holds system navigation group
    public SystemGroup: NavigationGroup = null;
    // Holds system navigation items
    public SystemSection: NavigationSection = null;
    // Holds system default navigation item
    public SystemDefaultSection: NavigationSection = null;

    // System navigation items
    private _SystemSearchNavigationItem: NavigationItem = null;
    public set SystemSearchNavigationItem(item: NavigationItem) {
        this._SystemSearchNavigationItem = item;
    }
    public get SystemSearchNavigationItem(): NavigationItem {
        return this._SystemSearchNavigationItem;
    }

    private _SystemCurrentNavigationItem: NavigationItem = null;
    public set SystemCurrentNavigationItem(item: NavigationItem) {
        this._SystemCurrentNavigationItem = item;
    }
    public get SystemCurrentNavigationItem(): NavigationItem {
        return this._SystemCurrentNavigationItem;
    }

    public SystemNewNavigationItem: NavigationItem = null;
    public SystemCurrentTopicNavigationItem: NavigationItem = null;
    public SystemCurrentAssignmentNavigationItem: NavigationItem = null;
    public SystemDefaultNavigationItem: NavigationItem = null;
    public DefaultNavigationItem: NavigationItem = null;

    public get FirstNavigationItem(): NavigationItem {
        let firstNavigationItem: NavigationItem = null;
        exit:
        if (this.Navigation) {
            for (let navGroup of this.Navigation) {
                if (navGroup?.Items?.length) {
                    for (let navSection of navGroup?.Items) {
                        if (navSection.ID != NavigationSection.SYSTEM_DEFAULT_SECTION_ID && navSection.ID != NavigationSection.SYSTEM_SECTION_ID) {
                            if (navSection.Items && navSection.Items.length > 0) {
                                firstNavigationItem = navSection.Items[0];
                                break exit;
                            }
                        }
                    }
                }
            }
        }
        return firstNavigationItem;
    }

    public initializeNavigation(nav?: any) {
        this.NavigationItemsDict = {};

        if (!nav) {
            nav = localStorage.getItem("Navigation");
            nav = JSON.parse(nav);
        }

        for (let app in nav) {

            let systemGroup: NavigationGroup = null;
            let systemSection: NavigationSection = null;
            let systemDefaultSection: NavigationSection = null;
            let systemSearchNavigationItem: NavigationItem = null;
            let systemCurrentNavigationItem: NavigationItem = null;
            let systemCurrentTopicNavigationItem: NavigationItem = null;
            let systemCurrentAssignmentNavigationItem: NavigationItem = null;

            // Reset application navigation data
            this.ApplicationNavigationData[app] = {
                Default: null,
                IsNavigationChanged: false
            };

            let navigation: Array<NavigationGroup> = nav[app].sort((groupA: NavigationGroup, groupB: NavigationGroup) => {
                if (groupA.Order > groupB.Order) return 1;
                if (groupA.Order < groupB.Order) return -1;
                return 0;
            }).map((group: NavigationGroup) => {

                let navigationGroup: NavigationGroup = <NavigationGroup>Object.assign(new NavigationGroup, group);

                // Check for system group
                if (group.ID == NavigationGroup.SYSTEM) systemGroup = navigationGroup;

                if (group && group.Items && group.Items.length > 0) {
                    let navigationSections = group.Items.sort((sectionA: NavigationSection, sectionB: NavigationSection) => {
                        if (sectionA.Order > sectionB.Order) return 1;
                        if (sectionA.Order < sectionB.Order) return -1;
                        return 0;
                    }).map((section) => {
                        let navigationSection: NavigationSection = <NavigationSection>Object.assign(new NavigationSection, section);
                        // Check for system sections
                        if (navigationSection.ID == NavigationSection.SYSTEM_SECTION_ID) systemSection = navigationSection;

                        navigationSection.Hidden = false;

                        try {
                            let items = section.Items.filter((item) => {
                                return !!item["ViewState"];
                            }).map((item) => {
                                let navigationItem: NavigationItem = <NavigationItem>Object.assign(new NavigationItem, item);

                                // Check for system items
                                if (navigationItem.Guid == NavigationItem.SYSTEM_SEARCH_GUID) {
                                    systemSearchNavigationItem = navigationItem;
                                }
                                //if (navigationItem.Guid == NavigationItem.SYSTEM_NEW_VIEW_GUID) SystemNewNavigationItem = navigationItem;
                                if (navigationItem.Guid == NavigationItem.SYSTEM_CURRENT_VIEW_GUID) {
                                    systemCurrentNavigationItem = navigationItem;
                                }
                                if (navigationItem.Guid == NavigationItem.SYSTEM_CURRENT_TOPIC_GUID) {
                                    systemCurrentTopicNavigationItem = navigationItem;
                                }
                                if (navigationItem.Guid == NavigationItem.SYSTEM_CURRENT_ASSIGNMENT_GUID) {
                                    systemCurrentAssignmentNavigationItem = navigationItem;
                                }

                                navigationItem.SectionPath = navigationSection.Label.replace(/\W+/g, "_").toLowerCase();
                                navigationItem.GroupPath = navigationGroup.Label.replace(/\W+/g, "_").toLowerCase();
                                navigationItem.SectionID = section.ID;

                                try {
                                    if (this.nomWebServicesJson.platformConvertor.useFor["logon"]) {
                                        const serviceRecord: ServiceRecord = ApiServiceRecord.instance(item["ViewState"]);
                                        navigationItem.ViewState = this.nomWebServicesJson.platformConvertor.fromServiceViewStateRecord(serviceRecord);
                                    } else {
                                        const serviceRecord: ServiceRecord = ServiceRecord.instance(item["ViewState"]);
                                        navigationItem.ViewState = this.nomWebServicesJson.Convertor.fromServiceViewStateRecord(serviceRecord);
                                    }

                                    if (navigationSection.ID != NavigationSection.SYSTEM_SECTION_ID) {
                                        navigationItem.Guid = navigationItem.ViewState.guid;
                                    }

                                    DataStore.set(navigationItem.ViewState);
                                } catch (e) {
                                    return null;
                                }

                                if (navigationSection.ID != NavigationSection.SYSTEM_SECTION_ID
                                    && navigationSection.ID != NavigationSection.SYSTEM_DEFAULT_SECTION_ID) {

                                    navigationItem.Label = "Undefined";
                                    if (navigationItem.ViewState && navigationItem.ViewState.title) {
                                        const label = "PSTWebView";
                                        let navItemTitle = (this.languageSettings.get(`${label}_${navigationItem.Path}_${navigationItem.ViewState.title}`).includes(label)) ? navigationItem.ViewState.title : this.languageSettings.get(`${label}_${navigationItem.Path}_${navigationItem.ViewState.title}`);
                                        navigationItem.Label = navItemTitle;
                                    }
                                    navigationItem.Url = ['/PST', app, navigationItem.GroupPath, navigationItem.SectionPath, navigationItem.ViewState.guid];

                                    this.addToNavigationItemDict(navigationItem.ViewState.guid, navigationGroup.ID, navigationSection.ID);
                                }

                                if (navigationItem.IsDefault && !navigationItem.isSystemDefaultViewEmpty()) {
                                    this.ApplicationNavigationData[app].Default = navigationItem;
                                }

                                return navigationItem;
                            }).filter((item: NavigationItem) => {
                                return !!item;
                            }).sort((item1: NavigationItem, item2: NavigationItem) => {
                                return item1.Order - item2.Order;
                            });

                            navigationSection.Items = items;
                        } catch (e) {
                            console.log(e);
                        }

                        return navigationSection;
                    });

                    navigationGroup.Items = navigationSections;
                }

                return navigationGroup;
            });

            if (!systemGroup) {
                systemGroup = this.createSystemGroup(navigation);
            }
            if (!systemSection) {
                systemSection = this.createSystemSection(systemGroup.Items);
            }
            if (!systemSearchNavigationItem) {
                systemSearchNavigationItem = this.createSystemSearchNavigationItem(app, systemSection);
            }
            if (!systemCurrentNavigationItem) {
                systemCurrentNavigationItem = this.createSystemCurrentNavigationItem(app, systemSection);
            }
            if (!systemCurrentTopicNavigationItem) {
                systemCurrentTopicNavigationItem = this.createSystemCurrentTopicNavigationItem(app, systemSection);
            }
            if (!systemCurrentAssignmentNavigationItem) {
                systemCurrentAssignmentNavigationItem = this.createSystemCurrentAssignmentNavigationItem(app, systemSection);
            }

            navigation = navigation.sort((group1: NavigationGroup, group2: NavigationGroup) => {
                return group1.Order - group2.Order;
            });

            this.ApplicationNavigation[app] = navigation;

            this.NavigationSource.next(navigation);
        }

        this.nomWebServicesJson.Navigation = this.ApplicationNavigation;

        this.nomWebServicesJson.saveNavigationSessionData();
    }

    public initializeTeamNavigation(nav: any) {
        for (let app in nav) {
            if (app != this.Application) continue;
            let sections: Array<NavigationSection> = nav[app].map((section) => {
                let navigationSection: NavigationSection = <NavigationSection>Object.assign(new NavigationSection, section);

                navigationSection.Hidden = false;

                try {
                    let items = section.Items.filter((item) => {
                        return !!item["ViewState"];
                    }).map((item) => {
                        let navigationItem: NavigationItem = <NavigationItem>Object.assign(new NavigationItem, item);

                        navigationItem.SectionPath = navigationSection.Label.replace(/\W+/g, "_").toLowerCase();
                        navigationItem.GroupPath = 'team';
                        navigationItem.SectionID = section.ID;


                        try {
                            if (this.nomWebServicesJson.platformConvertor.useFor["logon"]) {
                                const serviceRecord: ServiceRecord = ApiServiceRecord.instance(item["ViewState"]);
                                navigationItem.ViewState = this.nomWebServicesJson.platformConvertor.fromServiceViewStateRecord(serviceRecord);
                            } else {
                                const serviceRecord: ServiceRecord = ServiceRecord.instance(item["ViewState"]);
                                navigationItem.ViewState = this.nomWebServicesJson.Convertor.fromServiceViewStateRecord(serviceRecord);
                            }

                            if (navigationSection.ID != NavigationSection.SYSTEM_SECTION_ID) {
                                navigationItem.Guid = navigationItem.ViewState.guid;
                            }

                            DataStore.set(navigationItem.ViewState);
                        } catch (e) {
                            return null;
                        }

                        if (navigationSection.ID != NavigationSection.SYSTEM_SECTION_ID
                            && navigationSection.ID != NavigationSection.SYSTEM_DEFAULT_SECTION_ID) {

                            navigationItem.Label = "Undefined";
                            if (navigationItem.ViewState && navigationItem.ViewState.title) {
                                const label = "PSTWebView";
                                let navItemTitle = (this.languageSettings.get(`${label}_${navigationItem.Path}_${navigationItem.ViewState.title}`).includes(label)) ? navigationItem.ViewState.title : this.languageSettings.get(`${label}_${navigationItem.Path}_${navigationItem.ViewState.title}`);
                                navigationItem.Label = navItemTitle;
                            }

                            navigationItem.Url = ['/PST', app, 'team', navigationItem.SectionPath, navigationItem.ViewState.guid];
                        }

                        if (navigationItem.IsDefault && !navigationItem.isSystemDefaultViewEmpty()) {
                            this.ApplicationNavigationData[app].Default = navigationItem;
                        }

                        return navigationItem;
                    }).filter((item: NavigationItem) => {
                        return !!item;
                    }).sort((item1: NavigationItem, item2: NavigationItem) => {
                        return item1.Order - item2.Order;
                    });

                    navigationSection.Items = items;
                } catch (e) {
                    console.log(e);
                }

                return navigationSection;
            });

            let navigation: Array<NavigationGroup> = [];

            for (let group of this.Navigation) {
                if (group.ID != NavigationGroup.TEAM) {
                    navigation.push(group)
                } else {
                    // Remove prev navigation items from dictionary
                    for (let section of group.Items) {
                        for (let item of section.Items) {
                            this.removeFromNavigationItemDict(item.ViewState.guid, group.ID, section.ID);
                        }
                    }
                    // Add new navigation items to dictionary
                    for (let section of sections) {
                        for (let item of section.Items) {
                            this.addToNavigationItemDict(item.ViewState.guid, group.ID, section.ID);
                        }
                    }

                    group.Items = sections;
                    navigation.push(group)
                }
            }
            this.Navigation = navigation;
        }
    }

    private removeFromNavigationItemDict(guid: string, groupID: number, sectionID: number) {
        let index = -1;
        if (this.NavigationItemsDict[guid]) {
            if (this.NavigationItemsDict[guid].find((elem: string, idx: number) => {
                if (elem === ("" + groupID + "|" + sectionID + "|" + guid)) {
                    index = idx;
                    return true;
                }
                return false;
            })) {
                this.NavigationItemsDict[guid].splice(index, 1);
            }
        }
    }

    public initializeNavigationForDesktop() {
        // @TODO
        /*
        let nav = JSON.parse(localStorage.getItem("Navigation"));
    
        for (let app in nav) {
            // Reset application navigation data
            this.ApplicationNavigationData[app] = {
                Default: null,
                IsNavigationChanged: false
            };
    
            let navigation: Array<NavigationSection> = new Array<NavigationSection>();
    
            if (app == "playbook") {
                let systemSection = this.createSystemSection(navigation);
                this.createSystemCurrentTopicNavigationItem(app, systemSection);
            }
    
            this.ApplicationNavigation[app] = navigation;
        }
    
        this.nomWebServicesJson.Navigation = this.ApplicationNavigation;
        this.nomWebServicesJson.saveNavigationSessionData();
        */
    }

    private createSystemGroup(navigation: Array<NavigationGroup>): NavigationGroup {
        let systemGroup: NavigationGroup = new NavigationGroup();
        systemGroup.ID = NavigationGroup.SYSTEM;
        systemGroup.Label = "System";
        systemGroup.Order = -1;
        systemGroup.Items = [];
        systemGroup.Opened = false;
        navigation.push(systemGroup);
        return systemGroup;
    }

    private createSystemSection(navigation: Array<NavigationSection>): NavigationSection {
        let systemSection: NavigationSection = new NavigationSection();
        systemSection.ID = NavigationSection.SYSTEM_SECTION_ID;
        systemSection.Label = "System";
        systemSection.Hidden = true;
        systemSection.Order = -2;
        navigation.push(systemSection);
        return systemSection;
    }

    private createSystemDefaultSection(navigation: Array<NavigationSection>): NavigationSection {
        let systemDefaultSection: NavigationSection = new NavigationSection();
        systemDefaultSection.ID = NavigationSection.SYSTEM_DEFAULT_SECTION_ID;
        systemDefaultSection.Label = "DEFAULT";
        systemDefaultSection.Hidden = true;
        systemDefaultSection.Order = -1;
        navigation.push(systemDefaultSection);
        return systemDefaultSection;
    }

    public createNewSection(group: NavigationGroup): NavigationSection {

        let currentGroup: NavigationGroup = this.Navigation.find((g: NavigationGroup) => {
            return g.ID == group.ID;
        });
        let newSection: NavigationSection = new NavigationSection();
        let tmp = currentGroup.Items.map((section) => { return section.ID; });
        let max = Math.max(...tmp);
        newSection.ID = max + 1;
        newSection.Label = `${this.languageSettings.PSTWebNewSectionLabel}`;
        newSection.Hidden = false;
        newSection.Order = 999999 + newSection.ID;
        newSection.inEditMode = true;
        newSection.isNewSection = true;
        newSection.Opened = true;
        currentGroup.Items.push(newSection);
        return newSection;
    }

    public removeSection(group: NavigationGroup, section: NavigationSection) {

    }

    private createSystemSearchNavigationItem(app: string, section: NavigationSection): NavigationItem {
        let database = this.nomWebServicesJson.getDatabase();
        let path = this.nomWebServicesJson.getPath(this.inEditTeamSettingsMode);
        // Create system search navigation item
        let vs: SearchViewStateRecord = null;
        let type: number = ViewStateTypes.SEARCH;
        if (app == 'playbook') {
            type = ViewStateTypes.SEARCH;
        }
        if (app == 'discover') {
            type = ViewStateTypes.WIRESEARCH;
        }
        if (app == 'journal') {
            type = ViewStateTypes.DRAFTSEARCH;
        }

        vs = RecordFactory.createViewStateForType<SearchViewStateRecord>(type);

        vs.guid = NavigationItem.SYSTEM_SEARCH_GUID;
        vs.viewStateFilterState = true;
        vs.database = database;
        vs.path = new Path(path);
        vs.viewStateSearchQuery = "";
        vs.title = 'SYSTEM_SEARCH';
        vs.viewStateFilter = ViewStateFilterSerializer.Instance(this.globalTablesService).initialize(type);
        vs.viewStateSorting = ViewStateOptionsSerializer.Instance(this.globalTablesService).initialize(type, PSTFieldNames.PSTViewStateSorting);
        vs.viewStateGrouping = ViewStateOptionsSerializer.Instance(this.globalTablesService).initialize(type, PSTFieldNames.PSTViewStateGrouping);

        let item: NavigationItem = new NavigationItem();
        item.SectionID = NavigationSection.SYSTEM_SECTION_ID;
        item.Label = "SYSTEM_SEARCH";
        item.Database = database;
        item.Path = path;
        item.Guid = NavigationItem.SYSTEM_SEARCH_GUID;
        item.Url = ['/PST', app, 'search', NavigationItem.SYSTEM_SEARCH_GUID];
        item.Hidden = false;
        item.System = true;
        item.ViewState = vs;
        item.Order = -1;
        item.IsDefault = false;

        section.Items.push(item);

        return item;
    }

    private createSystemNewNavigationItem(app: string, section: NavigationSection) {
        let database = this.nomWebServicesJson.getDatabase();
        let path = this.nomWebServicesJson.getPath(this.inEditTeamSettingsMode);
        // Create system new navigation item
        let vs: TopicViewStateRecord = new TopicViewStateRecord();
        vs.guid = NavigationItem.SYSTEM_NEW_VIEW_GUID;
        vs.viewStateFilterState = true;
        vs.database = database;
        vs.path = new Path(path);
        vs.title = 'NEW_VIEW';

        let item: NavigationItem = new NavigationItem();
        item.SectionID = NavigationSection.SYSTEM_SECTION_ID;
        item.Label = "NEW_VIEW";
        item.Database = database;
        item.Path = path;
        item.Guid = NavigationItem.SYSTEM_NEW_VIEW_GUID;
        item.Url = ['/PST', app, 'system', 'new'];
        item.Hidden = false;
        item.System = true;
        item.ViewState = vs;
        item.Order = -2;
        item.IsDefault = false;

        section.Items.push(item);
    }

    public createNewNavigationItem(groupID: number, sectionID: number, vs: ViewStateRecord): NavigationItem {

        let group = this.Navigation.find((group: NavigationGroup) => {
            return group.ID == groupID;
        });
        if (group) {
            let section = group.Items.find((section: NavigationSection) => {
                return section.ID == sectionID;
            });
            if (section) {
                let database = this.nomWebServicesJson.getDatabase();

                let item: NavigationItem = new NavigationItem();
                item.SectionID = section.ID;
                item.Label = vs.title;
                item.Database = database;
                item.Path = vs.path.fullPath;
                item.Guid = vs.guid;
                item.Hidden = false;
                item.System = false;
                item.ViewState = vs;
                item.SectionPath = section.Label.replace(/\W+/g, "_").toLowerCase();
                item.GroupPath = group.Label.replace(/\W+/g, "_").toLowerCase();
                item.Url = ['/PST', this.Application, item.GroupPath, item.SectionPath, item.Guid];

                item.IsDefault = false;
                section.Items.push(item);
                return item;
            }
        }

        return null;
    }

    private createSystemCurrentNavigationItem(app: string, section: NavigationSection): NavigationItem {
        let database = this.nomWebServicesJson.getDatabase();
        let path = this.nomWebServicesJson.getPath(this.inEditTeamSettingsMode);

        let vs: ViewStateRecord = new ViewStateRecord();
        vs.guid = NavigationItem.SYSTEM_CURRENT_VIEW_GUID;
        vs.viewStateFilterState = true;
        vs.database = database;
        vs.path = new Path(path);
        vs.title = 'CURRENT_VIEW';

        let item: NavigationItem = new NavigationItem();
        item.SectionID = NavigationSection.SYSTEM_SECTION_ID;
        item.Label = "CURRENT_VIEW";
        item.Database = database;
        item.Path = path;
        item.Guid = NavigationItem.SYSTEM_CURRENT_VIEW_GUID;
        item.Url = ['/PST', app, 'system', 'current', NavigationItem.SYSTEM_CURRENT_VIEW_GUID];
        item.Hidden = false;
        item.System = true;
        item.ViewState = vs;
        item.Order = -3;
        item.IsDefault = false;

        section.Items.push(item);
        return item;
    }

    private createSystemCurrentTopicNavigationItem(app: string, section: NavigationSection): NavigationItem {
        let database = this.nomWebServicesJson.getDatabase();
        let path = this.nomWebServicesJson.getPath(this.inEditTeamSettingsMode);
        let vs: SingleTopicViewStateRecord = new SingleTopicViewStateRecord();
        vs.guid = NavigationItem.SYSTEM_CURRENT_TOPIC_GUID;
        vs.viewStateFilterState = true;
        vs.database = database;
        vs.path = new Path(path);
        vs.viewStateFilter = ViewStateFilterSerializer.Instance(this.globalTablesService).initialize(ViewStateTypes.TOPIC);
        vs.viewStateSorting = ViewStateOptionsSerializer.Instance(this.globalTablesService).initialize(ViewStateTypes.TOPIC, PSTFieldNames.PSTViewStateSorting);
        vs.viewStateGrouping = ViewStateOptionsSerializer.Instance(this.globalTablesService).initialize(ViewStateTypes.TOPIC, PSTFieldNames.PSTViewStateGrouping);
        vs.title = 'CURRENT_TOPIC';

        let item: NavigationItem = new NavigationItem();
        item.SectionID = NavigationSection.SYSTEM_SECTION_ID;
        item.Label = "CURRENT_TOPIC";
        item.Database = database;
        item.Path = path;
        item.Guid = NavigationItem.SYSTEM_CURRENT_TOPIC_GUID;
        item.Url = ['/PST', app, 'system', 'topic'];
        item.Hidden = false;
        item.System = true;
        item.ViewState = vs;
        item.Order = -4;
        item.IsDefault = false;

        section.Items.push(item);
        return item;
    }

    private createSystemCurrentAssignmentNavigationItem(app: string, section: NavigationSection) {
        let database = this.nomWebServicesJson.getDatabase();
        let path = this.nomWebServicesJson.getPath(this.inEditTeamSettingsMode);
        let vs: AssignmentViewStateRecord = new AssignmentViewStateRecord();
        vs.guid = NavigationItem.SYSTEM_CURRENT_ASSIGNMENT_GUID;
        vs.viewStateFilterState = true;
        vs.database = database;
        vs.path = new Path(path);
        vs.title = 'CURRENT_ASSIGNMENT';

        let item: NavigationItem = new NavigationItem();
        item.SectionID = NavigationSection.SYSTEM_SECTION_ID;
        item.Label = "CURRENT_ASSIGNMENT";
        item.Database = database;
        item.Path = path;
        item.Guid = NavigationItem.SYSTEM_CURRENT_ASSIGNMENT_GUID;
        item.Url = ['/PST', app, 'system', 'assignment'];
        item.Hidden = false;
        item.System = true;
        item.ViewState = vs;
        item.Order = -5;
        item.IsDefault = false;

        section.Items.push(item);
        return item;
    }

    private createSystemViewStateRecord(guid: string, title: string): ViewStateRecord {
        let database = this.nomWebServicesJson.getDatabase();
        let path = this.nomWebServicesJson.getPath(this.inEditTeamSettingsMode);

        let vs: ViewStateRecord = new ViewStateRecord();
        vs.guid = guid;
        vs.viewStateFilterState = true;
        vs.database = database;
        vs.path = new Path(path);
        vs.title = title;
        vs.viewStateType = ViewStateTypes.SYSTEM;

        return vs;
    }

    private createSystemDefaultNavigationItem(app: string, section: NavigationSection) {
        let database = this.nomWebServicesJson.getDatabase();
        let path = this.nomWebServicesJson.getPath(this.inEditTeamSettingsMode);

        let item: NavigationItem = new NavigationItem();
        item.SectionID = NavigationSection.SYSTEM_DEFAULT_SECTION_ID;
        item.Label = "DEFAULT";
        item.Database = database;
        item.Path = path;
        item.Guid = NavigationItem.SYSTEM_DEFAULT_VIEW_GUID;
        item.Url = ['/PST', app, 'system', 'default'];
        item.Hidden = false;
        item.System = true;
        item.ViewState = this.createSystemViewStateRecord(NavigationItem.SYSTEM_DEFAULT_VIEW_GUID, 'DEFAULT');
        item.Order = -1;
        item.IsDefault = true;

        section.Items.push(item);
    }

    public async getTeamNavigation(database: string, team: string): Promise<ServiceResponse<any>> {
        let response: HttpResponse<any> = null;
        if (this.nomWebServicesJson.platformConvertor.useFor["getNavigation"]) {
        } else {
            try {
                response = await this.nomWebServicesJson.get(
                    "/api/PSTNavigation",
                    {
                        headers: new HttpHeaders({
                            "Content-Type": "application/json",
                            "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                        }),
                        responseType: 'json',
                        observe: 'response',
                        params: {
                            database: database,
                            team: team
                        },
                    });
            } catch (e) {
                throw e;
            }
        }
        return new ServiceResponse<any>(true, response.body);
    }

    public async getNavigation(): Promise<ServiceResponse<any>> {
        let response: HttpResponse<any> = null;
        if (this.nomWebServicesJson.platformConvertor.useFor["getNavigation"]) {
        } else {
            try {
                response = await this.nomWebServicesJson.get(
                    "/api/PSTNavigation",
                    {
                        headers: new HttpHeaders({
                            "Content-Type": "application/json",
                            "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                        }),
                        responseType: 'json',
                        observe: 'response'
                    });
            } catch (e) {
                throw e;
            }
        }
        return new ServiceResponse<any>(true, response.body);
    }

    public async updateNavigation(sections: Array<NavigationSection>, type: NavigationLayoutTypes, application?: string, team?: string): Promise<ServiceResponse<boolean>> {
        let response: HttpResponse<any> = null;
        try {
            application = application || this.Application;
            response = await this.nomWebServicesJson.put(
                "/api/PSTNavigation",
                {
                    Application: application,
                    Navigation: sections,
                    NavigationLayoutType: type,
                    Team: (team ? team : "")
                },
                {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    observe: 'response'
                });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to update PST navigation!",
                detailMessage: `Failed to update PST navigation! \n\rStackTrace: NomWebServicesJson.updatePSTNavigation(); \n\rError: ${error}`
            });
            return new ServiceResponse<boolean>(false, false);
        }
        return new ServiceResponse<boolean>(true, response.body);
    }

    public async checkTeamSettingsState() {
        while (this.exitingTeamSettingsMode) {
            await (new Promise(resolve => setTimeout(resolve, 50)));
        }
    }

    public async updateNavigationSection() {
        this.ApplicationNavigationData[this.Application].IsNavigationChanged = true;
        await this.saveNavigation();
    }

    public removeNavigationSection(section: NavigationSection) {
        // @TODO
        /*
        this.ApplicationNavigationData[this.Application].IsNavigationChanged = true;
    
        let sectionIdx: number = -1;
        let i: number = 0;
        for (let s of this.Navigation) {
            sectionIdx++;
            if (s.ID == section.ID) {
                sectionIdx = i;
                break;
            }
            i++;
        }
    
        if (sectionIdx > -1) {
            this.Navigation.splice(sectionIdx, 1);
        }
    
        this.NavigationSource.next(this.Navigation);
        */
    }

    public async makeDefaultNavigationItem(group: NavigationGroup, section: NavigationSection, item: NavigationItem) {
        this.resetDefaultNavigationItem(this.getNavigationGroup(group.ID));
        item.IsDefault = true;
        await this.saveNavigation();
    }

    private resetDefaultNavigationItem(group: NavigationGroup) {
        for (let group of this.Navigation) {
            for (let section of group.Items) {
                for (let item of section.Items) {
                    item.IsDefault = false;
                }
            }
        }
    }

    private getNavigationGroup(groupID: number): NavigationGroup {
        return this.Navigation.find((group: NavigationGroup) => {
            return group.ID == groupID;
        });
    }

    public async saveNavigationItem(item: NavigationItem, vs: ViewStateRecord): Promise<ServiceResponse<ViewStateRecord>> {
        let res: ServiceResponse<ViewStateRecord> = await this.viewStateService.updateViewStateRecord(vs);
        if (res.status) {
            item.Label = vs.title;
            item.ViewState = res.data;
            this.nomWebServicesJson.saveNavigationSessionData();

            return res;
        }

        return null;
    }

    public async saveCurrentNavigationItem(): Promise<ServiceResponse<ViewStateRecord>> {
        if (this.CurrentNavigationItem) {
            if (this.currentListView) this.currentListView.showLoader();
            let res: ServiceResponse<ViewStateRecord> = null;
            if (this.CurrentNavigationItem.isSystemSearch() || this.CurrentNavigationItem.isNewView()) {
                if (this.CurrentNavigationItem.isSystemSearch()) {
                    this.viewStateService.ViewStateRecord.title = (<SearchViewStateRecord>this.viewStateService.ViewStateRecord).viewStateSearchQuery;
                }
                res = await this.viewStateService.createViewStateRecord(this.viewStateService.NavigationViewStateRecord);
            } else {
                res = await this.viewStateService.updateViewStateRecord(this.viewStateService.NavigationViewStateRecord);
            }
            if (res.status) {
                res.data.copyDataTo(this.CurrentNavigationItem.ViewState, true);
                res.data.copyDataTo(this.viewStateService.NavigationViewStateRecord, true);
                this.nomWebServicesJson.saveNavigationSessionData();
                if (this.viewStateService.NavigationViewStateRecord instanceof SlideshowViewStateRecord) {
                    this.viewStateService.slideshowService.initialize(this.viewStateService.NavigationViewStateRecord);
                    this.viewStateService.slideshowService.next();
                }
            }
            if (this.currentListView) this.currentListView.hideLoader();
            return res;
        }

        return null;
    }

    public async deleteNavigationItem(vs: ViewStateRecord, groups: Array<number>): Promise<ServiceResponse<Record>> {
        await this.removeNavigationItem(vs, groups);
        let res = await this.viewStateService.deleteViewStateRecord(vs);
        return res;
    }

    public async removeNavigationItem(vs: ViewStateRecord, groups: Array<number>) {
        let items: Array<{ group: NavigationGroup, section: NavigationSection, item: NavigationItem, index: number }> = [];
        let index: number = -1;

        for (let group of this.Navigation) {
            if (groups.indexOf(group.ID) < 0) {
                // No rights to remove from this group
                continue;
            }
            for (let section of group.Items) {
                index = -1;
                for (let item of section.Items) {
                    index++;
                    if (item.ViewState.guid == vs.guid) {
                        items.push({
                            group: group,
                            section: section,
                            item: item,
                            index: index
                        });
                    }
                }
            }
        }

        for (let item of items) {
            item.section.Items.splice(item.index, 1);
            this.removeFromNavigationItemDict(item.item.ViewState.guid, item.group.ID, item.section.ID);
        }

        await this.saveNavigation();
        this.nomWebServicesJson.saveNavigationSessionData();
    }

    private getLastEnterpriseNavigationItemIndex(section: NavigationSection): number {
        let targetIdx: number = - 1;
        let idx: number = 0;
        for (let item of section.Items) {
            if (!item.ViewState.path.isEnterpriseView) {
                targetIdx = idx;
                break;
            }
            idx++;
        }
        targetIdx = idx;
        return targetIdx;
    }

    private getLastTeamNavigationItemIndex(section: NavigationSection): number {
        let targetIdx: number = - 1;
        let idx: number = 0;
        for (let item of section.Items) {
            if (!item.ViewState.path.isTeamView) {
                targetIdx = idx;
                break;
            }
            idx++;
        }
        targetIdx = idx;
        return targetIdx;
    }

    private getNavigationItemIndex(section: NavigationSection, targetNavigationItem: NavigationItem): number {
        let targetIdx: number = - 1;
        let idx: number = 0;
        for (let item of section.Items) {
            if (item.Guid == targetNavigationItem.Guid) {
                targetIdx = idx;
                break;
            }
            idx++;
        }
        return targetIdx;
    }

    public async reorderNavigation(group: NavigationGroup, sourceSection: NavigationSection, targetSection: NavigationSection, sourceIndex: number, targetIndex: number) {

        if (sourceSection && targetSection && sourceIndex > -1 && targetIndex > -1) {
            let navigationItem = sourceSection.Items.splice(sourceIndex, 1)[0];
            navigationItem.SectionID = targetSection.ID;
            targetSection.Items.splice(targetIndex, 0, navigationItem);

            await this.saveNavigation();
        }
    }

    public async addToNavigation(groupID: number, sectionID: number, vs: ViewStateRecord) {
        this.createNewNavigationItem(groupID, sectionID, vs);

        await this.saveNavigation(groupID);

        this.addToNavigationItemDict(vs.guid, groupID, sectionID);
    }

    public async saveNavigation(groupID?: number) {
        for (let group of this.Navigation) {

            if (group.ID == NavigationGroup.SYSTEM) {
                continue;
            }

            let navigation = this.getNavigationCopy(group.Items);
            if (group.ID == NavigationGroup.GLOBAL && (!groupID || groupID == group.ID)) {
                let result = await this.updateNavigation(navigation, NavigationLayoutTypes.Enterprise);
                if (result.status) {
                    this.nomWebServicesJson.saveNavigationSessionData();
                }
            }
            if (group.ID == NavigationGroup.TEAM && (!groupID || groupID == group.ID)) {
                let result = await this.updateNavigation(navigation, NavigationLayoutTypes.Group, this.Application, this.currentPlaybookTeam);
                if (result.status) {
                    this.nomWebServicesJson.saveNavigationSessionData();
                }
            }
            if (group.ID == NavigationGroup.PERSONAL && (!groupID || groupID == group.ID)) {
                let result = await this.updateNavigation(navigation, NavigationLayoutTypes.User);
                if (result.status) {
                    this.nomWebServicesJson.saveNavigationSessionData();
                }
            }
        }

        this.NavigationSource.next(this.Navigation);
    }

    public async loadNavigation() {
        let result = await this.getNavigation();
        if (result.status) {
            this.initializeNavigation(result.data);
            this.setApplication(this.Application);
        } else {
            console.log("Failed to load navigation ...");
            console.log(result.message);
        }
    }
    public async loadTeamNavigation(database: string, team: string) {
        let result = await this.getTeamNavigation(database, team);
        if (result.status) {
            this.initializeTeamNavigation(result.data);
        } else {
            console.log("Failed to load team navigation ...");
            console.log(result.message);
        }
    }

    private getNavigationCopy(nav: Array<NavigationSection>): Array<NavigationSection> {
        // Fix order field & update navigation
        let sorder: number = 0;
        let order: number = 0;

        let navigation: Array<NavigationSection> = new Array<NavigationSection>();

        if (nav) {
            for (let section of nav) {
                section.Order = sorder;
                sorder++;
                for (let item of section.Items) {
                    item.Order = order;
                    // Don't apply user navigation offset for the default view
                    /*if (section.ID != NavigationSection.SYSTEM_DEFAULT_SECTION_ID
                        && item.ViewState
                        && item.ViewState.path
                        && (item.ViewState.path.isOwnView || !item.System)
                    ) {
                        item.Order = NavigationItem.USER_NAVIGATION_ITEMS_OFFSET + order;
                    }*/
                    order++;
                }
            }


            for (let section of nav) {
                // Skip system temp section & new section
                if (section.ID == NavigationSection.SYSTEM_SECTION_ID || section.ID == NavigationSection.SYSTEM_NEW_SECTION_ID) {
                    continue;
                }

                // Skip system default section if default view is empty
                if (
                    section.ID == NavigationSection.SYSTEM_DEFAULT_SECTION_ID &&
                    (section.Items.length == 0 || section.Items[0].isSystemDefaultViewEmpty())
                ) {
                    continue;
                }

                let copy: NavigationSection = section.deepCopy();
                navigation.push(section.deepCopy());
            }
        }

        return navigation;
    }

    public async loadNavigationItem(item: NavigationItem) {
        if (this.CurrentNavigationItem && this.CurrentNavigationItem.Guid == item.Guid) {
            await this.setNavigationItem(item);
        } else {
            this.router.navigate(item.Url);
        }
    }

    public async updateViewStateRecord(groupID: number, sectionID: number, vsRecord: ViewStateRecord) {
        // Check if we need to move from group & section
        let currentSection: NavigationSection;
        let currentGroup: NavigationGroup;
        let currentItem: NavigationItem;

        for (let group of this.Navigation) {
            for (let section of group.Items) {
                for (let item of section.Items) {
                    if (item.ViewState.guid == vsRecord.guid) {
                        currentGroup = group;
                        currentSection = section;
                        currentItem = item;
                        break;
                    }
                }
            }
        }

        await this.viewStateService.updateViewStateRecord(vsRecord);

        if ((currentGroup && currentSection && currentItem) && (groupID != currentGroup.ID || sectionID != currentSection.ID)) {
            this.removeNavigationItem(currentItem.ViewState, [groupID]);
            let navItem = this.createNewNavigationItem(groupID, sectionID, vsRecord);
            await this.loadNavigationItem(navItem);
        }
    }

    public async loadAndCreateViewStateRecord(groupID: number, sectionID: number, vsRecord: ViewStateRecord) {
        let path = "";
        if (groupID == NavigationGroup.GLOBAL) {
            path = `P_${this.globalTablesService.EnterprisePlaybookGroup}\\VS`;
        }
        if (groupID == NavigationGroup.TEAM) {
            if (this.currentPlaybookTeam) path = `P_${this.currentPlaybookTeam}\\VS`;
            else path = `P_${this.globalTablesService.DefaultPlaybookTeam.ID}\\VS`;
        }
        if (groupID == NavigationGroup.PERSONAL || !groupID) {
            path = `U_${this.nomWebServicesJson.UserId}\\VS`;
        }
        vsRecord.fullPath = path;
        let res = await this.viewStateService.createViewStateRecord(vsRecord);

        if (res.status && !(groupID == null || sectionID == null)) {
            let navItem = this.createNewNavigationItem(groupID, sectionID, res.data);
            await this.loadNavigationItem(navItem);
            await this.saveNavigation();

            this.addToNavigationItemDict(navItem.ViewState.guid, groupID, sectionID);
        }
    }

    public async loadViewStateRecord(vs: ViewStateRecord) {
        let item: NavigationItem;

        if (!vs) return;

        if (this.NavigationItemsDict[vs.guid] && this.NavigationItemsDict[vs.guid].length > 0) {
            let elem = this.NavigationItemsDict[vs.guid][0].split("|");
            this.Navigation.find((g: NavigationGroup) => {
                if (g.ID.toString() === elem[0]) {
                    g.Items.find((s: NavigationSection) => {
                        if (s.ID.toString() === elem[1]) {
                            s.Items.find((i: NavigationItem) => {
                                if (i.Guid === elem[2]) {
                                    item = i;
                                }
                                return i.Guid === elem[2];
                            });
                        }
                        return s.ID.toString() === elem[1];
                    })
                }
                return g.ID.toString() === elem[0];
            });

        }
        if (!item) {
            this.SystemCurrentNavigationItem.ViewState = vs;
            item = this.SystemCurrentNavigationItem;
        }

        await this.loadNavigationItem(item);
        this.nomWebServicesJson.saveNavigationSessionData();
    }


    public enterSearchMode(searchTerm: string, initialLoad?: boolean, application?: string) {
        let prevSearchTerm = "";
        if (this.SystemSearchNavigationItem && this.SystemSearchNavigationItem.ViewState) {
            prevSearchTerm = (<SearchViewStateRecord>this.SystemSearchNavigationItem.ViewState).viewStateSearchQuery;
            (<SearchViewStateRecord>this.SystemSearchNavigationItem.ViewState).viewStateSearchQuery = searchTerm;
            (<SearchViewStateRecord>this.SystemSearchNavigationItem.ViewState).title = "Search - " + searchTerm;
            this.nomWebServicesJson.saveNavigationSessionData();
        }
        if (!application) {
            if (searchTerm == prevSearchTerm && !(this.currentListView instanceof ManageTeamMembers)) {
                this.setNavigationItem(this.SystemSearchNavigationItem);
            } else {
                this.router.navigate(['/PST', this.Application, 'system', 'search', encodeURIComponent(searchTerm)]);
            }
        } else {
            this.router.navigate(['/PST', application, 'system', 'search', encodeURIComponent(searchTerm)]);
        }
    }

    public navigateToParentTopic(title: string, guid: any, onInitial?: boolean) {
        // @TODO
        /*
        let url = ['/PST', 'playbook', 'topic', encodeURIComponent(guid)];
        const playbookNavigation = this.ApplicationNavigation['playbook'];
        const systemSection = playbookNavigation.find((section) => {
            return section.ID == NavigationSection.SYSTEM_SECTION_ID;
        });
        const systemCurrentTopicNavigationItem = systemSection.Items.find((item: NavigationItem) => {
            return item.Guid == NavigationItem.SYSTEM_CURRENT_TOPIC_GUID;
        });
        systemCurrentTopicNavigationItem.ViewState.title = title;
        (<SingleTopicViewStateRecord>systemCurrentTopicNavigationItem.ViewState).topicGuid = guid;
        systemCurrentTopicNavigationItem.Url = url;
        this.router.navigate(url);
        */
    }

    public navigateToDefaultItem() {
        if (this.DefaultNavigationItem) {
            let url = this.DefaultNavigationItem.Url;
            this.router.navigate(url);
        } else {
            let navItem = this.FirstNavigationItem;
            if (navItem) {
                let url = navItem.Url;
                this.router.navigate(url);
            }
        }
    }

    // About menu
    public showAbout(event: any) {
        this.isAboutshown = { flag: true, event: event };
        this.showAboutSource.next(this.isAboutshown);
    }

    public hideAbout(event: any) {
        this.isAboutshown = { flag: false, event: event };
        this.showAboutSource.next(this.isAboutshown);
    }

    // Create new view
    public openCreateNewView(event) {
        this.newViewShown = { flag: true, event: event };
        this.newViewShownSource.next(this.newViewShown);
    }

    public closeCreateNewView(event: any) {
        this.newViewShown = { flag: false, event: event };
        this.newViewShownSource.next(this.newViewShown);
    }

    // Create new template
    public openCreateNewTemplate(event) {
        this.newTemplateShown = { flag: true, event: event };
        this.newTemplateShownSource.next(this.newViewShown);
    }

    public closeCreateNewTemplate(event: any) {
        this.newTemplateShown = { flag: false, event: event };
        this.newTemplateShownSource.next(this.newViewShown);
    }

    public async saveTemplateSettings(templateRecord) {
        this.newTemplateShown = { flag: true, event: true, templateRecord: templateRecord };
        this.newTemplateShownSource.next(this.newTemplateShown);
    }

    public setApplication(application: string) {
        this.Application = this.viewStateService.applicationName = application;

        this.Navigation = null;
        this.SystemSection = this.SystemDefaultSection = null;

        this.SystemSearchNavigationItem = null;
        this.SystemNewNavigationItem = null;
        this.SystemCurrentNavigationItem = null;
        this.SystemCurrentTopicNavigationItem = null;
        this.SystemCurrentAssignmentNavigationItem = null;
        this.DefaultNavigationItem = null;

        if (this.ApplicationNavigation[application]) {
            this.Navigation = this.ApplicationNavigation[application];

            this.SystemGroup = this.ApplicationNavigation[application].find((group: NavigationGroup) => {
                return group.ID == NavigationGroup.SYSTEM;
            });
            if (this.SystemGroup) {
                this.SystemSection = this.SystemGroup.Items.find((section: NavigationSection) => {
                    return section.ID == NavigationSection.SYSTEM_SECTION_ID;
                });
                if (this.SystemSection) {
                    this.SystemSearchNavigationItem = this.SystemSection.Items.find((item: NavigationItem) => {
                        return item.Guid == NavigationItem.SYSTEM_SEARCH_GUID;
                    });
                    this.SystemCurrentTopicNavigationItem = this.SystemSection.Items.find((item: NavigationItem) => {
                        return item.Guid == NavigationItem.SYSTEM_CURRENT_TOPIC_GUID;
                    });
                    this.SystemCurrentAssignmentNavigationItem = this.SystemSection.Items.find((item: NavigationItem) => {
                        return item.Guid == NavigationItem.SYSTEM_CURRENT_ASSIGNMENT_GUID;
                    });
                    this.SystemCurrentNavigationItem = this.SystemSection.Items.find((item: NavigationItem) => {
                        return item.Guid == NavigationItem.SYSTEM_CURRENT_VIEW_GUID;
                    });
                } else {
                    this.SystemSearchNavigationItem = null;
                    this.SystemNewNavigationItem = null;
                    this.SystemCurrentNavigationItem = null;
                    this.SystemCurrentTopicNavigationItem = null;
                    this.SystemCurrentAssignmentNavigationItem = null;
                    this.DefaultNavigationItem = null;
                }
            }

            if (this.SystemDefaultSection) {
                this.SystemDefaultNavigationItem = this.SystemDefaultSection.Items.find((item: NavigationItem) => {
                    return item.SectionID == NavigationSection.SYSTEM_DEFAULT_SECTION_ID;
                });
            } else {
                this.SystemDefaultNavigationItem = null;
            }
            if (this.ApplicationNavigationData[application].Default) {
                this.DefaultNavigationItem = this.ApplicationNavigationData[application].Default;
            }
        }

        this.NavigationSource.next(this.Navigation);
    }

    private addToNavigationItemDict(guid: string, groupID: number, sectionID: number) {
        if (!this.NavigationItemsDict[guid]) this.NavigationItemsDict[guid] = [];
        this.NavigationItemsDict[guid].push(groupID + "|" + sectionID + "|" + guid);
    }
}
